import React, { useState } from "react";
import { aboutMeta } from "../data/mock";
import { ArrowUpRight, Award } from "lucide-react";
import JourneyModal from "./JourneyModal";

// ---------------------------------------------------------------------------
// PortraitDisc — circular subject on a warm peach glow
// ---------------------------------------------------------------------------
const PortraitDisc = () => (
  <div className="relative">
    <div className="relative aspect-square w-full max-w-[440px] mx-auto">
      {/* Warm peach glow behind the disc */}
      <div
        aria-hidden
        className="absolute -inset-[12%] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 55%, rgba(255, 165, 110, 0.35) 0%, rgba(255, 165, 110, 0.12) 35%, rgba(255, 165, 110, 0) 65%)",
          filter: "blur(20px)",
        }}
      />
      {/* Warm near-black backdrop disc */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full shadow-[0_30px_70px_-25px_rgba(60,30,15,0.45)]"
        style={{ background: "#1a1410" }}
      />
      {/* Soft top-left highlight */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 80% at 30% 25%, rgba(255,220,180,0.10) 0%, rgba(255,220,180,0) 55%)",
        }}
      />
      {/* Circular photo with cream ring */}
      <div
        className="absolute inset-[3%] rounded-full overflow-hidden"
        style={{
          boxShadow:
            "0 0 0 2px rgba(245, 222, 179, 0.85), 0 20px 50px -20px rgba(0,0,0,0.5)",
        }}
      >
        <img
          src={aboutMeta.profileImage}
          alt="Shweta Chauhan — Product Designer"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          style={{
            objectPosition: "50% 50%",
            transform: "scale(1.6) translateY(14%)",
            transformOrigin: "50% 50%",
            filter: "brightness(1.18) contrast(1.08) saturate(1.05)",
          }}
        />
      </div>
    </div>

    {/* Floating "Open to roles" badge */}
    <div className="hidden sm:flex absolute -bottom-2 right-2 lg:right-6 items-center gap-2 px-3.5 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.18)] dark:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      <span className="text-[12.5px] text-neutral-700 dark:text-neutral-300">Open to roles</span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// AboutActions — Journey CTA + Certificate link
// ---------------------------------------------------------------------------
const AboutActions = ({ onOpenJourney }) => (
  <div className="mt-9 flex flex-wrap gap-3">
    <button
      type="button"
      onClick={onOpenJourney}
      data-testid="about-journey-button"
      className="group inline-flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 hover:bg-neutral-700 dark:hover:bg-neutral-200 text-[14.5px] font-medium px-5 py-3 rounded-full transition-colors duration-200"
    >
      More about my journey
      <ArrowUpRight
        className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        strokeWidth={2.25}
      />
    </button>
    <a
      href={aboutMeta.certificateHref}
      target="_blank"
      rel="noreferrer"
      data-testid="about-certificate-link"
      className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-[14px] text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors duration-200"
    >
      <Award className="w-4 h-4" strokeWidth={1.75} />
      {aboutMeta.certificate}
    </a>
  </div>
);

// ---------------------------------------------------------------------------
// AboutCopy — text column
// ---------------------------------------------------------------------------
const AboutCopy = ({ onOpenJourney }) => (
  <div className="lg:col-span-7 lg:pt-6">
    <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
      About
    </div>
    <p className="mt-4 text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.005em] text-neutral-600 dark:text-neutral-400 font-normal">
      {aboutMeta.intro}
    </p>
    <h2 className="mt-3 text-[44px] sm:text-[60px] lg:text-[72px] leading-[1.0] tracking-[-0.035em] font-medium text-neutral-900 dark:text-neutral-50">
      {aboutMeta.firstName}
      <br />
      {aboutMeta.lastName}
    </h2>
    <p className="mt-5 text-[17px] lg:text-[18px] tracking-[-0.005em] text-neutral-600 dark:text-neutral-400 font-medium">
      {aboutMeta.title}
    </p>
    <p className="mt-6 text-[16px] lg:text-[17px] text-neutral-700 dark:text-neutral-300 leading-[1.7] max-w-[620px]">
      {aboutMeta.bio}
    </p>
    <AboutActions onOpenJourney={onOpenJourney} />
  </div>
);

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------
const About = () => {
  const [open, setOpen] = useState(false);
  return (
    <section id="about" className="py-24 lg:py-32 border-t border-neutral-100 dark:border-neutral-800">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <PortraitDisc />
          </div>
          <AboutCopy onOpenJourney={() => setOpen(true)} />
        </div>
      </div>
      <JourneyModal
        open={open}
        onClose={() => setOpen(false)}
        certificateHref={aboutMeta.certificateHref}
      />
    </section>
  );
};

export default About;
